import React, { useEffect, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Button, Grid, TextField } from '@mui/material';
import { SideBar, useSideBar } from '../../components/SideBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api } from '../../api';
import { faBars, faMagnifyingGlass, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useStyles } from '../../hooks/useStyles';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid';
import { EditUserGroupDialog, useEditUserGroupDialog } from './EditUserGroupDialog';
import { UserGroup } from '../../api/user-group';
import { useGlobal } from '../../units/global/GlobalProvider';
import { useAuth } from '../../units/auth/AuthProvider';
import { grey } from '@mui/material/colors';

export const useUserGroupManagePage = () => {
    const page = usePage();
    const classes = useStyles();
    const auth = useAuth();
    const { userGroups, setUserGroups, reloadUserGroups } = useGlobal();
    const sideBar = useSideBar();
    const editUserGroupDialog = useEditUserGroupDialog({ ...page });
    const [inputSearchConfig, setInputSearchConfig] = useState('');
    const columns: GridColDef[] = [
        { field: 'userGroupId', headerName: 'userGroupId', flex: 3, minWidth: 90, cellClassName: 'cell', hide: true },
        { field: 'id', headerName: '群組ID', flex: 3, minWidth: 90, cellClassName: 'cell', hide: false },
        { field: 'descr', headerName: '群組描述', flex: 3, minWidth: 90, cellClassName: 'cell', hide: false },
        { field: 'authority', headerName: '優先級', flex: 3, minWidth: 90, cellClassName: 'cell', hide: false },
        {
            field: 'action',
            renderHeader: (params: GridColumnHeaderParams) => <>操作</>,
            width: 180,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as UserGroup;
                return (
                    <Grid container justifyContent={'space-around'}>
                        <Button
                            disabled={![auth.profile.authority >= (row.authority || 0)].every((v) => v)}
                            variant='contained'
                            className={classes.buttonWhite}
                            onClick={() => {
                                editUserGroupDialog.setUserGroupId(row.userGroupId || '');
                                editUserGroupDialog.setInputId(row.id || '');
                                editUserGroupDialog.setInputDescr(row.descr || '');
                                editUserGroupDialog.setInputAuthority(row.authority || 0);
                                editUserGroupDialog.onOpen();
                            }}
                        >
                            編輯
                        </Button>
                        <Button
                            disabled={![auth.profile.authority > (row.authority || 0)].every((v) => v)}
                            variant='contained'
                            className={classes.buttonRed}
                            onClick={() =>
                                page.alert.show({
                                    type: 'warning',
                                    title: '警告',
                                    message: '確定要刪除？',
                                    onConfirm: async () => {
                                        page.setIsLoading(true);
                                        const ret = await api.userGroup
                                            .remove({ userGroupId: row.userGroupId! })
                                            .finally(() => page.setIsLoading(false));
                                        if (ret.success) {
                                            reloadUserGroups();
                                            page.simpleAlert.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                                        } else {
                                            page.simpleAlert.show({
                                                autoHideDuration: 6000,
                                                severity: 'error',
                                                message: JSON.stringify(ret),
                                            });
                                        }
                                    },
                                })
                            }
                        >
                            刪除
                        </Button>
                    </Grid>
                );
            },
        },
    ];
    useEffect(() => {
        reloadUserGroups();
    }, []);
    return { page, classes, sideBar, editUserGroupDialog, inputSearchConfig, setInputSearchConfig, userGroups, columns };
};

export const UserGroupManagePage = (): React.ReactElement => {
    const { page, classes, sideBar, editUserGroupDialog, inputSearchConfig, setInputSearchConfig, userGroups, columns } =
        useUserGroupManagePage();
    return (
        <Page {...page} title='使用者群組管理'>
            <Grid container flexDirection={'row'} height={'100%'}>
                <Grid container item flex={1} flexDirection={'column'} padding={4} sx={{ backgroundColor: grey[100] }}>
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <TextField
                                value={inputSearchConfig}
                                onChange={(e) => setInputSearchConfig(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        {`搜尋(${['使用者名稱'].join(', ')})`}
                                    </>
                                }
                                style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                            />
                            <Button
                                className={classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={() => editUserGroupDialog.onOpen()}
                            >
                                <FontAwesomeIcon icon={faPlus} fixedWidth />
                                {'新增使用者群組'}
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container item flex={1} padding={2} borderRadius={2} marginTop={2} sx={{ backgroundColor: 'white' }}>
                        <DataGrid
                            rows={(() => {
                                const searchs = inputSearchConfig.split(/\s+/).filter((v) => v.length > 0);
                                return userGroups.filter((v, i, a) =>
                                    [
                                        searchs.length == 0
                                            ? true
                                            : searchs
                                                  .map(
                                                      (s) =>
                                                          v.id?.toUpperCase().includes(s.toUpperCase()) ||
                                                          v.descr?.toUpperCase().includes(s.toUpperCase()) ||
                                                          v.authority?.toString().includes(s.toUpperCase()),
                                                  )
                                                  .every((v) => v),
                                    ].every((v) => v),
                                );
                            })()}
                            columns={columns}
                            getRowId={(row: UserGroup) => row.userGroupId!}
                            pageSize={10}
                            rowsPerPageOptions={[10]}
                            experimentalFeatures={{ newEditingApi: true }}
                            disableColumnMenu={true}
                            disableSelectionOnClick={true}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <EditUserGroupDialog {...editUserGroupDialog} />
        </Page>
    );
};
